import { type RootState } from '@/store';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import type { FeeItem } from '@/apis/extended/admin/interestRateApi';
import { calculateAPRWithFees } from '@/utils/aprUtils';
import {
  getBridgeRateByTerm,
  getCurrentInterestRate,
} from '@/utils/calculator/rateIntegration';
import {
  computeFeeBreakdown,
  computeMergedFeeBreakdown,
  type CalculatedFeeRule,
} from '@/utils/fees/feeEngine';
import { type LoanType } from '@/utils/rateMatrixUtils';
import type { ProductEsitmation } from '@/types/ProductType';

export type RateQuoteInput = {
  loanProgram: USER_LOAN_PROGRAM;
  // Offer price basis preferred; falls back to listPrice
  offerPrice?: number;
  listPrice: number;
  downPayment: number;
  creditScore: number;
  loanTermYears: number;
  /** Bridge program: term in months (6, 12, 18, 24) for term-specific pricing */
  bridgeTermMonths?: number;
  // When available, use real estimation outputs for precise closing cost/APR behavior
  estimation?: ProductEsitmation;
};

export type RateQuoteOutput = {
  loanType: LoanType;
  purchasePrice: number;
  loanAmount: number;
  ltv: number;
  interestRate: number; // percent
  apr: number | null; // percent
};

export function mapUserLoanProgramToLoanType(
  loanProgram: USER_LOAN_PROGRAM
): LoanType {
  const map: Record<USER_LOAN_PROGRAM, LoanType> = {
    [USER_LOAN_PROGRAM.CONVENTIONAL]: 'Conventional',
    [USER_LOAN_PROGRAM.FHA]: 'FHA',
    [USER_LOAN_PROGRAM.VA]: 'VA',
    [USER_LOAN_PROGRAM.USDA]: 'USDA',
    [USER_LOAN_PROGRAM.DSCR]: 'DSCR',
    [USER_LOAN_PROGRAM.COMMERCIAL]: 'Commercial',
    [USER_LOAN_PROGRAM.FIX_AND_FLIP]: 'FixAndFlip',
    [USER_LOAN_PROGRAM.BRIDGE]: 'Bridge',
    [USER_LOAN_PROGRAM.HELOC]: 'HELOC',
    [USER_LOAN_PROGRAM.HELOAN]: 'HELOAN',
    [USER_LOAN_PROGRAM.SECOND_MORTGAGE]: 'SecondMortgage',
    [USER_LOAN_PROGRAM.CASH]: 'Cash',
    [USER_LOAN_PROGRAM.DEFAULT]: 'Conventional',
  };
  return map[loanProgram] || 'Conventional';
}

export function computeRateQuote(
  state: RootState,
  input: RateQuoteInput
): RateQuoteOutput | null {
  const purchasePrice =
    input.offerPrice && input.offerPrice > 0
      ? input.offerPrice
      : input.listPrice;
  const loanAmount = Math.max(0, purchasePrice - (input.downPayment || 0));
  const ltv = purchasePrice > 0 ? loanAmount / purchasePrice : 0;

  const loanType = mapUserLoanProgramToLoanType(input.loanProgram);
  const creditScore = input.creditScore || 700;

  let interestRate: number;
  if (
    loanType === 'Bridge' &&
    input.bridgeTermMonths != null &&
    Number.isFinite(input.bridgeTermMonths)
  ) {
    interestRate = getBridgeRateByTerm(
      state,
      input.bridgeTermMonths,
      creditScore,
      ltv
    );
  } else {
    interestRate = getCurrentInterestRate(state, loanType, creditScore, ltv);
  }

  if (!Number.isFinite(interestRate)) {
    return null;
  }

  // Pull admin fee items from redux (loaded from backend config)
  const feeItems: FeeItem[] =
    ((state.interestRate as any)?.feeItemsByLoanType?.[
      loanType
    ] as FeeItem[]) || [];

  let breakdown;
  if (input.estimation) {
    const calculatedFeeRules: CalculatedFeeRule[] =
      ((state.interestRate as any)?.calculatedFeeRulesByLoanType?.[
        loanType
      ] as CalculatedFeeRule[]) || [];
    breakdown = computeMergedFeeBreakdown({
      estimation: input.estimation,
      calculatedFeeRules,
      feeItems,
      loanAmount,
    });
  } else {
    // Fallback: adjustments only (used in contexts where estimation isn't available)
    breakdown = computeFeeBreakdown(feeItems, loanAmount);
  }

  // Partition APR-included fees into origination vs other based on category
  const included = Object.values(breakdown.byCategory)
    .flat()
    .filter((f) => f.includedInApr);
  const originationFees = included
    .filter((f) => f.category === 'lender')
    .reduce((sum, f) => sum + f.computedAmount, 0);
  const otherFees = included
    .filter((f) => f.category !== 'lender')
    .reduce((sum, f) => sum + f.computedAmount, 0);

  let apr: number | null = null;
  try {
    // IMPORTANT: Use explicit-fee APR calculation (no hidden loan-type defaults).
    // This prevents DSCR from ever inheriting VA/FHA defaults unless a fee item exists.
    const loanTermMonths =
      loanType === 'Bridge' && input.bridgeTermMonths != null
        ? input.bridgeTermMonths
        : (input.loanTermYears || 30) * 12;

    const result = calculateAPRWithFees({
      interestRate,
      loanAmount,
      loanTermMonths,
      originationFees,
      otherFees,
      loanType: loanType as any,
    });
    apr = result.apr;
  } catch {
    apr = null;
  }

  return {
    loanType,
    purchasePrice,
    loanAmount,
    ltv,
    interestRate,
    apr,
  };
}
